import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Container,
  Fade,
  Slide
} from '@mui/material';
import MobileOptimizedProductList from '../../components/store/MobileOptimizedProductList';
import FeaturedProducts from '../../components/store/FeaturedProducts';
import BestSellerProducts from '../../components/store/BestSellerProducts';
import CategoryShowcase from '../../components/store/CategoryShowcase';
import HomeSlider from '../../components/store/HomeSlider';
import InfoBar from '../../components/store/InfoBar';
import { StoreBanner, ProductPromotion } from '../../components/store';
import { Promotion } from '../../components/store/ProductPromotion';
import { Banner } from '../../components/store/StoreBanner';
import { useStoreSettings } from '../../contexts/StoreSettingsContext';

interface StorePageContext {
  searchQuery?: string;
  selectedCategoryId?: string;
}

const MobileOptimizedStorePage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { searchQuery, selectedCategoryId } = useOutletContext<StorePageContext>();
  const { settings } = useStoreSettings();

  const [banners, setBanners] = useState<Banner[]>([]);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);

  // بيانات تجريبية للبانرات والعروض
  const defaultBanners: Banner[] = [
    {
      id: '1',
      title: 'عروض خاصة',
      subtitle: 'خصومات تصل إلى 50%',
      imageUrl: '/images/banner1.jpg',
      buttonText: 'تسوق الآن',
      buttonLink: '/offers'
    },
    {
      id: '2',
      title: 'منتجات جديدة',
      subtitle: 'اكتشف أحدث المنتجات',
      imageUrl: '/images/banner2.jpg',
      buttonText: 'اكتشف المزيد',
      buttonLink: '/categories'
    }
  ];

  const defaultPromotions: Promotion[] = [
    {
      id: '1',
      title: 'خصم 30% على الإلكترونيات',
      description: 'عرض محدود لفترة قصيرة',
      imageUrl: '/images/promo1.jpg',
      buttonText: 'تسوق الآن',
      buttonLink: '/categories'
    }
  ];

  useEffect(() => {
    // محاكاة تحميل البيانات
    const loadData = async () => {
      setLoading(true);
      // هنا يمكن إضافة استدعاء API حقيقي
      await new Promise(resolve => setTimeout(resolve, 1000));
      setBanners(defaultBanners);
      setPromotions(defaultPromotions);
      setLoading(false);
    };

    loadData();
  }, []);

  // إذا كان هناك بحث أو فئة محددة، عرض النتائج فقط
  if (searchQuery || selectedCategoryId) {
    return (
      <Container maxWidth={false} disableGutters={isMobile}>
        <Box sx={{ py: 2 }}>
          {searchQuery && (
            <Fade in timeout={500}>
              <Typography variant="h5" gutterBottom sx={{ px: 2, fontWeight: 'bold' }}>
                نتائج البحث عن: "{searchQuery}"
              </Typography>
            </Fade>
          )}

          {selectedCategoryId && (
            <Fade in timeout={500}>
              <Typography variant="h5" gutterBottom sx={{ px: 2, fontWeight: 'bold' }}>
                منتجات الفئة المختارة
              </Typography>
            </Fade>
          )}

          <MobileOptimizedProductList
            categoryId={selectedCategoryId}
            searchQuery={searchQuery}
            itemsPerPage={isMobile ? 8 : 12}
          />
        </Box>
      </Container>
    );
  }

  // عرض الصفحة الرئيسية
  return (
    <Box sx={{ width: '100%' }}>
      {/* السلايدر الرئيسي - للهاتف فقط */}
      {isMobile && (
        <Slide in direction="down" timeout={600}>
          <Box sx={{ mb: 2 }}>
            <HomeSlider />
          </Box>
        </Slide>
      )}

      {/* البانرات */}
      {banners.length > 0 && (
        <Fade in timeout={800}>
          <Box sx={{ mb: 3 }}>
            <StoreBanner banners={banners} />
          </Box>
        </Fade>
      )}

      {/* شريط المعلومات */}
      <Slide in direction="up" timeout={700}>
        <Box sx={{ mb: 3 }}>
          <InfoBar />
        </Box>
      </Slide>

      {/* عرض الأقسام - محسن للهاتف */}
      <Fade in timeout={900}>
        <Box sx={{ mb: 4 }}>
          <CategoryShowcase
            title="تسوق حسب الأقسام"
          />
        </Box>
      </Fade>

      {/* العروض الترويجية */}
      {promotions.length > 0 && (
        <Slide in direction="up" timeout={1000}>
          <Box sx={{ mb: 4 }}>
            <ProductPromotion promotions={promotions} />
          </Box>
        </Slide>
      )}

      {/* المنتجات المميزة */}
      <Fade in timeout={1100}>
        <Box sx={{ mb: 4 }}>
          <FeaturedProducts
            title="المنتجات المميزة"
            maxItems={isMobile ? 4 : 8}
          />
        </Box>
      </Fade>

      {/* المنتجات الأكثر مبيعاً */}
      <Slide in direction="up" timeout={1200}>
        <Box
          sx={{
            mb: 4,
            bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
            py: 4,
            borderRadius: isMobile ? 0 : 3,
            mx: isMobile ? -1 : 0
          }}
        >
          <Container maxWidth={false} disableGutters={isMobile}>
            <BestSellerProducts
              title="الأكثر مبيعاً"
              maxItems={isMobile ? 4 : 6}
            />
          </Container>
        </Box>
      </Slide>

      {/* جميع المنتجات */}
      <Fade in timeout={1300}>
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              px: isMobile ? 2 : 0,
              fontWeight: 'bold',
              mb: 3,
              textAlign: 'center'
            }}
          >
            جميع المنتجات
          </Typography>

          <MobileOptimizedProductList
            itemsPerPage={isMobile ? 8 : 12}
          />
        </Box>
      </Fade>

      {/* شريط معلومات سفلي */}
      <Slide in direction="up" timeout={1400}>
        <Box sx={{ mt: 4, mb: isMobile ? 2 : 4 }}>
          <InfoBar />
        </Box>
      </Slide>
    </Box>
  );
};

export default MobileOptimizedStorePage;
